// App.tsx
import React from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";

import PageWithLayout from "./components/PageWithLayout";
import Login from "./components/Login";
import Help from "./components/Help";
import Account from "./components/Account";
import Organization from "./components/Organization";
import AcceptInvite from "./components/AcceptInvite";
import ProtectedRoute from "./components/ProtectedRoute";
import Onboarding from "./components/Onboarding";
import Analytics from "./components/Analytics";
import NewsletterEdition from "./components/NewsletterEdition";
import Posts from "./components/Posts";
import Blueprint from "./components/Blueprint";
import Blueprints from "./components/Blueprints";
import Interviewer from "./components/Interviewer";
import Insight from "./components/Insight";
import Insights from "./components/Insights";
import Sources from "./components/Sources";
import ContentTools from "./components/ContentTools";
import Subscribers from "./components/Subscribers";
import SubscriberDetail from "./components/SubscriberDetail";
import Settings from "./components/Settings";
import Requests from "./components/Requests";
import RequestsList from "./components/RequestsList";
import SingleRequest from "./components/SingleRequest";
import Journeys from "./components/Journeys";
import JourneyDetail from "./components/JourneyDetail";
import Segments from "./components/Segments";
import SegmentDetail from "./components/SegmentDetail";
import Brand from "./components/Brand";
import InterviewJourneyGenerator from "./components/InterviewJourneyGenerator";
import ContentInterview from "./components/ContentInterview";
import InterviewList from "./components/ContentInterview/InterviewList";
import SelfInterview from "./components/SelfInterview";
import Launch from "./components/Launch";
import Templates from "./components/Templates";
import TemplateDetail from "./components/TemplateDetail";
import StrategyDocumentImport from "./components/StrategyDocumentImport";
import CustomFieldDefinitions from "./components/CustomFieldDefinitions";
import ChooseTemplate from "./components/ChooseTemplate";
import MfaVerification from "./components/mfa/MfaVerification";
import InstantNewsletter from "./components/InstantNewsletter";
import TeamMemberDetail from "./components/TeamMemberDetail";
import EditionAnalytics from "./components/EditionAnalytics";
import PageTimeTracker from "./components/PageTimeTracker";

import { ReduxStateProvider } from "./context/ReduxStateProvider";
import StatsigMetricsProvider from "./context/StatsigMetricsProvider";

const App: React.FC = () => {
  return (
    <ReduxStateProvider>
      <StatsigMetricsProvider>
        <Router>
          <PageTimeTracker />
          <Routes>
            <Route
              path="/"
              element={
                <PageWithLayout>
                  <Login />
                </PageWithLayout>
              }
            />
            <Route
              path="/login"
              element={
                <PageWithLayout>
                  <Login />
                </PageWithLayout>
              }
            />
            <Route
                path="/help"
                element={
                  <PageWithLayout>
                    <Help />
                  </PageWithLayout>
                }
            />
            <Route
              path="/mfa-verify"
              element={
                <MfaVerification />
              }
            />
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route
              path="/invite"
              element={
                <PageWithLayout>
                  <AcceptInvite />
                </PageWithLayout>
              }
            />
            <Route
              path="/brand"
              element={
                <PageWithLayout>
                  <Brand />
                </PageWithLayout>
              }
            />
            <Route
              path="/instant"
              element={
                <PageWithLayout>
                  <InstantNewsletter />
                </PageWithLayout>
              }
            />
            <Route
              path="/templates"
              element={
                <PageWithLayout>
                  <Templates />
                </PageWithLayout>
              }
            />
            <Route
              path="/templates/:id"
              element={
                <PageWithLayout>
                  <TemplateDetail />
                </PageWithLayout>
              }
            />
            <Route
              element={
                <ProtectedRoute redirectPathWhenUnauthenticated="/login" />
              }>
              <Route
                path="/onboarding"
                element={
                  <PageWithLayout>
                    <Onboarding />
                  </PageWithLayout>
                }
              />
            </Route>
            <Route
              element={<ProtectedRoute redirectPathWhenUnauthenticated="/login" />}>
              <Route
                path="/analytics"
                element={
                  <PageWithLayout>
                    <Analytics />
                  </PageWithLayout>
                }
              />
              <Route
                path="/sources"
                element={
                  <PageWithLayout>
                    <Sources />
                  </PageWithLayout>
                }
              />
              <Route
                path="/content-tools"
                element={
                  <PageWithLayout>
                    <ContentTools />
                  </PageWithLayout>
                }
              />
              <Route
                path="/requests"
                element={
                  <PageWithLayout>
                    <Requests />
                  </PageWithLayout>
                }
              />
              <Route
                path="/requests/list"
                element={
                  <PageWithLayout>
                    <RequestsList />
                  </PageWithLayout>
                }
              />
              <Route
                path="/requests/view/:requestId"
                element={
                  <PageWithLayout>
                    <SingleRequest />
                  </PageWithLayout>
                }
              />
              <Route
                path="/blueprints/import-strategy"
                element={
                  <PageWithLayout>
                    <StrategyDocumentImport />
                  </PageWithLayout>
                }
              />
              <Route
                path="/blueprints/:blueprintId"
                element={
                  <PageWithLayout>
                    <Blueprint />
                  </PageWithLayout>
                }
              />
              <Route
                path="/blueprints"
                element={
                  <PageWithLayout>
                    <Blueprints />
                  </PageWithLayout>
                }
              />
              <Route
                path="/subscribers"
                element={
                  <PageWithLayout>
                    <Subscribers />
                  </PageWithLayout>
                }
              />
              <Route
                path="/subscribers/custom-fields"
                element={
                  <PageWithLayout>
                    <CustomFieldDefinitions />
                  </PageWithLayout>
                }
              />
              <Route
                path="/subscribers/:subscriberId"
                element={
                  <PageWithLayout>
                    <SubscriberDetail />
                  </PageWithLayout>
                }
              />
              <Route
                path="/insights"
                element={
                  <PageWithLayout>
                    <Insights />
                  </PageWithLayout>
                }
              />
              <Route
                path="/insights/:insightId"
                element={
                  <PageWithLayout>
                    <Insight />
                  </PageWithLayout>
                }
              />
              <Route
                path="/interviewer"
                element={
                  <PageWithLayout>
                    <Interviewer />
                  </PageWithLayout>
                }
              />
              <Route
                path="/content-interviews"
                element={
                  <PageWithLayout>
                    <ContentInterview />
                  </PageWithLayout>
                }
              />
              <Route
                path="/content-interviews/list"
                element={
                  <PageWithLayout>
                    <InterviewList />
                  </PageWithLayout>
                }
              />
              <Route
                path="/self-interviews"
                element={
                  <PageWithLayout>
                    <SelfInterview />
                  </PageWithLayout>
                }
              />
              <Route
                path="/self-interviews/:id"
                element={
                  <PageWithLayout>
                    <SelfInterview />
                  </PageWithLayout>
                }
              />
              <Route
                path="/journeys"
                element={
                  <PageWithLayout>
                    <Journeys />
                  </PageWithLayout>
                }
              />
              <Route
                path="/journeys/generate-from-interview"
                element={
                  <PageWithLayout>
                    <InterviewJourneyGenerator />
                  </PageWithLayout>
                }
              />
              <Route
                path="/journeys/:journeyId"
                element={
                  <PageWithLayout>
                    <JourneyDetail />
                  </PageWithLayout>
                }
              />
              <Route
                path="/segments"
                element={
                  <PageWithLayout>
                    <Segments />
                  </PageWithLayout>
                }
              />
              <Route
                path="/segments/:segmentId"
                element={
                  <PageWithLayout>
                    <SegmentDetail />
                  </PageWithLayout>
                }
              />
              <Route
                path="/settings"
                element={
                  <PageWithLayout>
                    <Settings />
                  </PageWithLayout>
                }
              />
              <Route
                path="/editions/:editionId/analytics"
                element={
                  <PageWithLayout>
                    <EditionAnalytics />
                  </PageWithLayout>
                }
              />
              <Route
                path="/newsletterEdition/:newsletterEditionId"
                element={
                  <PageWithLayout>
                    <NewsletterEdition />
                  </PageWithLayout>
                }
              />
              <Route
                path="/newsletterEdition/new"
                element={
                  <PageWithLayout>
                    <NewsletterEdition />
                  </PageWithLayout>
                }
              />
              <Route
                path="/create-post"
                element={
                  <PageWithLayout>
                    <ChooseTemplate />
                  </PageWithLayout>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PageWithLayout>
                    <Posts />
                  </PageWithLayout>
                }
              />
              <Route
                path="/launch"
                element={
                  <PageWithLayout>
                    <Launch />
                  </PageWithLayout>
                }
              />
              <Route
                path="/account"
                element={
                  <PageWithLayout>
                    <Account />
                  </PageWithLayout>
                }
              />
              <Route
                path="/team"
                element={
                  <PageWithLayout>
                    <Organization />
                  </PageWithLayout>
                }
              />
              <Route
                path="/team/:id"
                element={
                  <PageWithLayout>
                    <TeamMemberDetail />
                  </PageWithLayout>
                }
              />
            </Route>

            <Route
              path="*"
              element={
                <PageWithLayout>
                  <Login />
                </PageWithLayout>
              }
            />
          </Routes>
        </Router>
      </StatsigMetricsProvider>
    </ReduxStateProvider>
  );
};

export default App;
